import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import '../../../cypress/support/commonWindowTypings';
import { css } from '@emotion/react';
import { Container, Stage } from '@pixi/react';
import { useContext, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useDevicePixelRatio } from 'use-device-pixel-ratio';
import { isConfigFlagEnabled } from '../../config';
import { MAX_RESOLUTION, MIN_STAGE_HEIGHT_PX, MIN_STAGE_WIDTH_PX } from '../../constants/constants';
import { InstanceProvider, useInstanceContext } from '../../context/instance/instanceContext';
import { ReloadInstanceProvider, useReloadInstance } from '../../context/reloadInstance/reloadInstanceContext';
import { WebSocketContext, WebSocketProvider } from '../../context/webSocket/webSocketContext';
import { useAppSelector } from '../../redux/store';
import { StoreProvider } from '../../redux/storeProvider';
import { useReduxStore } from '../../redux/useReduxStore';
import { POKER_STAGE_DEFAULT_BACKGROUND_COLOR_NUM } from '../../styles/colors';
import { isTestEnvironment } from '../../utils/utils';
import { ActionArrowsContainer } from './actionArrows/actionArrowsContainer';
import { BombsContainer } from './bombs/bombsContainer';
import { CommunityCardsGroupContainer } from './cards/communityCards/communityCardsGroupContainer';
import { StartTourneyButtonContainer } from './controls/pixi/startTourneyButtonContainer';
import { PokerGameControls } from './controls/pokerGameControls';
import { PixiAppMountHandler } from './pixiAppMountHandler';
import { PixiDevToolsWrapper } from './pixiDevToolsWrapper';
import { AllPlayerBubblesContainer } from './player/bubbles/allPlayerBubblesContainer';
import { MySeatBackgroundContainer } from './player/mySeatBackgroundContainer';
import { SideBetPhaseDisplayContainer } from './sideBetPhaseDisplayContainer';
import { SideBetTargetsContainer } from './sideBetTargetsContainer';
import { BetPotsContainer } from './table/betPots/betPotsContainer';
import { DealerButtonContainer } from './table/dealerButtonContainer';
import { PokerSeatsContainer } from './table/pokerSeatsContainer';
import { AllPotsContainer } from './table/pots/allPotsContainer';
import { SitButtonsContainer } from './table/sitButtonsContainer';
import { PokerRoomBackground } from './table/tableGraphics/pokerRoomBackground';
import { PokerTableDisplay } from './table/tableGraphics/pokerTableDisplay';
import { TableShakeAnimationContainer } from './tableShakeAnimationContainer';
const STAGE_CSS = css `
  position: absolute;
  left: 0;
  top: 0;
`;
function getResolution(isHiDpiAllowed, dpr, numInstances) {
    if (numInstances > 2) {
        // use lower resolution in localhost/testing environments to help performance
        return isTestEnvironment() ? 0.5 : 1;
    }
    if (isHiDpiAllowed) {
        return Math.min(dpr, MAX_RESOLUTION);
    }
    return 1;
}
export const PokerTableStage = () => {
    const reloadInstance = useReloadInstance();
    const { instanceId, numInstances } = useInstanceContext();
    const store = useReduxStore();
    const ws = useContext(WebSocketContext);
    const dpr = useDevicePixelRatio();
    const stageDimensions = useAppSelector((state) => state.gameDimensions.stageDimensions, shallowEqual);
    const isHiDpiAllowed = useAppSelector((state) => state.session.isHiDpiAllowed);
    const resolution = getResolution(isHiDpiAllowed, dpr, numInstances);
    const stageOptions = useMemo(() => {
        // TODO: Disable antialiasing globally and try replacing all "dynamic" graphics with https://github.com/pixijs/graphics-smooth
        const useAntialiasing = isConfigFlagEnabled('disableAntialiasing') ? false : resolution <= 1;
        return {
            autoStart: true,
            backgroundColor: POKER_STAGE_DEFAULT_BACKGROUND_COLOR_NUM,
            // TODO: Determine powerPreference 'low-power' differs from 'high-performance' in a meaningful way
            powerPreference: 'low-power',
            antialias: useAntialiasing,
            resolution,
            autoDensity: true
        };
    }, [resolution]);
    if (stageDimensions === undefined ||
        stageDimensions.w < MIN_STAGE_WIDTH_PX ||
        stageDimensions.h < MIN_STAGE_HEIGHT_PX) {
        // stage dimensions not (yet) determined, or too small to be usable
        return null;
    }
    const mainTableShakeContents = (_jsxs(_Fragment, { children: [_jsx(PokerTableDisplay, {}), _jsx(SideBetTargetsContainer, {}), _jsx(MySeatBackgroundContainer, {}), _jsx(DealerButtonContainer, {})] }));
    const playersTableShakeContents = _jsx(PokerSeatsContainer, {});
    const isTableShakeAnimationEnabled = isConfigFlagEnabled('tableShakeAnimations');
    return (_jsx(Stage, { width: resolution > 1 ? stageDimensions.w * resolution : stageDimensions.w, height: resolution > 1 ? stageDimensions.h * resolution : stageDimensions.h, options: stageOptions, css: STAGE_CSS, children: _jsx(ReloadInstanceProvider, { existingReloadInstance: reloadInstance, children: _jsx(InstanceProvider, { instanceId: instanceId, numInstances: numInstances, isInPixiStage: true, children: _jsx(StoreProvider, { store: store, children: _jsx(WebSocketProvider, { existingWebSocketManager: ws.webSocketManager, children: _jsxs(PixiDevToolsWrapper, { children: [_jsx(PixiAppMountHandler, {}), _jsxs(Container, { interactiveChildren: false, children: [_jsx(PokerRoomBackground, {}), isTableShakeAnimationEnabled ? (_jsx(TableShakeAnimationContainer, { handleAnimationRemovals: true, children: mainTableShakeContents })) : (mainTableShakeContents)] }), _jsx(SitButtonsContainer, {}), isTableShakeAnimationEnabled ? (_jsx(TableShakeAnimationContainer, { handleAnimationRemovals: false, children: playersTableShakeContents })) : (playersTableShakeContents), _jsxs(Container, { interactiveChildren: false, children: [_jsx(CommunityCardsGroupContainer, {}), _jsx(AllPotsContainer, {}), _jsx(BombsContainer, {}), _jsx(ActionArrowsContainer, {}), _jsx(AllPlayerBubblesContainer, {}), _jsx(BetPotsContainer, {}), _jsx(SideBetPhaseDisplayContainer, {})] }), _jsx(StartTourneyButtonContainer, {}), _jsx(PokerGameControls, {})] }) }) }) }) }) }));
};
